import { useEffect, useState } from "react";
import { adminApi, ADMIN_API } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, QrCode, Copy, RefreshCw, Download, MapPin, Smartphone, Printer, FileImage } from "lucide-react";

export default function AdminNda() {
  const [link, setLink] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    adminApi.get("/nda/link").then(r => setLink(r.data)).catch(() => toast.error("Erreur lien NDA"));
    adminApi.get("/nda/signatures").then(r => setRows(r.data.signatures || [])).catch(() => {});
  };
  useEffect(load, []);

  const rotate = async () => {
    if (!window.confirm("Renouveler le lien ? Les QR codes déjà diffusés cesseront de fonctionner.")) return;
    setBusy(true);
    try { await adminApi.post("/nda/rotate"); toast.success("Nouveau lien généré"); load(); }
    catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  const dl = async (id, name) => {
    const r = await adminApi.get(`/nda/signatures/${id}/pdf`, { responseType: "blob" });
    const u = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a"); a.href = u; a.download = `NDA-${name}.pdf`; a.click();
  };

  const dlSvg = async () => {
    try {
      const r = await adminApi.get("/nda/qr.svg", { responseType: "blob" });
      const u = window.URL.createObjectURL(new Blob([r.data], { type: "image/svg+xml" }));
      const a = document.createElement("a"); a.href = u; a.download = "qr-nda-empco.svg"; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Erreur export SVG"); }
  };

  const printPoster = () => {
    const png = `data:image/png;base64,${link.qr_png_base64}`;
    const w = window.open("", "_blank", "width=820,height=1120");
    if (!w) { toast.error("Autorisez les pop-ups pour imprimer"); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Affiche NDA EmpCo</title>
      <style>
        @page { size: A4; margin: 18mm; }
        * { box-sizing: border-box; }
        body { font-family: Georgia, serif; color: #0B1220; margin: 0; padding: 24mm; }
        .eyebrow { font-family: ui-monospace, Menlo, monospace; font-size: 11px; letter-spacing: .25em;
                   text-transform: uppercase; color: #4B5563; margin: 0 0 8mm; }
        h1 { font-size: 32pt; line-height: 1.1; margin: 0 0 10mm; letter-spacing: -0.02em; }
        .lead { font-size: 13pt; line-height: 1.5; color: #1f2937; margin: 0 0 14mm; max-width: 150mm; }
        .qr { display: flex; justify-content: center; align-items: center; margin: 6mm 0 14mm; }
        .qr img { width: 110mm; height: 110mm; }
        .url { font-family: ui-monospace, Menlo, monospace; font-size: 11pt; word-break: break-all;
               text-align: center; padding: 6mm; border: 1px solid #D1D5DB; border-radius: 4mm; }
        ul { font-size: 11pt; line-height: 1.6; margin: 8mm 0 0; padding-left: 6mm; }
        .foot { position: fixed; bottom: 10mm; left: 24mm; right: 24mm;
                font-family: ui-monospace, Menlo, monospace; font-size: 9pt; color: #4B5563;
                border-top: 1px solid #D1D5DB; padding-top: 3mm; display: flex; justify-content: space-between; }
      </style></head>
      <body>
        <p class="eyebrow">EmpCo Compliance · Confidentialité</p>
        <h1>Accord de non-divulgation</h1>
        <p class="lead">Scannez ce QR code avec votre smartphone pour prendre connaissance et signer
          électroniquement l'accord de non-divulgation. Le lien est strictement personnel et ne doit
          pas être rediffusé.</p>
        <div class="qr"><img src="${png}" alt="QR code NDA EmpCo"/></div>
        <div class="url">${link.url}</div>
        <ul>
          <li>Signature électronique conforme eIDAS (Règl. UE 910/2014) et art. 1366-1367 du Code civil.</li>
          <li>Vérification par code à usage unique envoyé par e-mail.</li>
          <li>Horodatage, IP, géolocalisation et empreinte SHA-256 enregistrés.</li>
        </ul>
        <div class="foot"><span>empco-compliance.eu</span><span>Document interne — ne pas afficher publiquement</span></div>
        <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
      </body></html>`);
    w.document.close();
  };

  if (!link) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-nda-page">
      <div>
        <p className="eyebrow mb-1">Confidentialité</p>
        <h1 className="text-3xl font-serif tracking-tight">NDA — page cachée et registre des signatures</h1>
      </div>

      <div className="hairline p-6 grid md:grid-cols-[auto_1fr] gap-6 items-start" data-testid="admin-nda-link">
        <img src={`data:image/png;base64,${link.qr_png_base64}`} alt="QR code NDA"
          className="w-44 h-44 rounded-lg border border-border bg-white p-2" data-testid="admin-nda-qr" />
        <div className="space-y-3 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <QrCode className="w-3.5 h-3.5" /> Lien secret à diffuser (non indexé)
          </p>
          <p className="font-mono text-sm break-all" data-testid="admin-nda-url">{link.url}</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-lg h-10" data-testid="admin-nda-copy"
              onClick={() => { navigator.clipboard.writeText(link.url); toast.success("Lien copié"); }}>
              <Copy className="w-4 h-4 mr-2" /> Copier le lien
            </Button>
            <Button variant="outline" className="rounded-lg h-10" data-testid="admin-nda-qr-dl"
              onClick={() => { const a = document.createElement("a"); a.href = `data:image/png;base64,${link.qr_png_base64}`; a.download = "qr-nda-empco.png"; a.click(); }}>
              <Download className="w-4 h-4 mr-2" /> PNG
            </Button>
            <Button variant="outline" className="rounded-lg h-10" data-testid="admin-nda-qr-svg" onClick={dlSvg}>
              <FileImage className="w-4 h-4 mr-2" /> SVG (impression)
            </Button>
            <Button variant="outline" className="rounded-lg h-10" data-testid="admin-nda-qr-print" onClick={printPoster}>
              <Printer className="w-4 h-4 mr-2" /> Imprimer l'affiche A4
            </Button>
            <Button variant="outline" className="rounded-lg h-10" onClick={rotate} disabled={busy} data-testid="admin-nda-rotate">
              <RefreshCw className="w-4 h-4 mr-2" /> Renouveler le lien
            </Button>
          </div>
          <p className="font-mono text-[11px] text-muted-foreground">
            Langues publiées : {(link.langs || []).join(" · ")} — seule la version française fait foi.
          </p>
        </div>
      </div>

      <div className="hairline p-6" data-testid="admin-nda-registry">
        <p className="eyebrow mb-4">Registre des signatures ({rows.length})</p>
        {rows.length === 0 && <p className="text-sm text-muted-foreground">Aucune signature enregistrée.</p>}
        <div className="space-y-3">
          {rows.map((r) => {
            const gd = r.geo_device || {}; const gi = r.geo_ip || {};
            return (
              <div key={r.id} className="rounded-lg border border-border p-4 space-y-1.5" data-testid={`admin-nda-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-serif text-base">{r.first_name} {r.last_name}{r.company ? ` — ${r.company}` : ""}</p>
                    <p className="font-mono text-[11px] text-muted-foreground">
                      {new Date(r.signed_at).toLocaleString("fr-FR")} · code envoyé à {r.otp_sent_to || r.email} · langue {r.lang}
                    </p>
                  </div>
                  <Button variant="outline" className="rounded-lg h-9" onClick={() => dl(r.id, r.last_name)} data-testid={`admin-nda-pdf-${r.id}`}>
                    <Download className="w-4 h-4 mr-2" /> PDF signé
                  </Button>
                </div>
                <p className="font-mono text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <Smartphone className="w-3 h-3" /> IP {r.ip || "—"} · {r.platform || "plateforme inconnue"}{r.mobile ? " · mobile" : ""} · {(r.user_agent || "").slice(0, 60)}
                </p>
                <p className="font-mono text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <MapPin className="w-3 h-3" />
                  {gd.latitude
                    ? `${gd.latitude.toFixed(5)}, ${gd.longitude.toFixed(5)} (±${Math.round(gd.accuracy || 0)} m, appareil)`
                    : gi.latitude ? `${gi.city || ""} ${gi.country_name || ""} (${gi.latitude}, ${gi.longitude}, via IP)` : "position non communiquée"}
                </p>
                <p className="font-mono text-[10px] text-muted-foreground break-all">SHA-256 · {r.document_hash}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="hairline p-6" data-testid="admin-nda-notes">
        <p className="eyebrow mb-3">Note d'usage (interne, jamais affichée au signataire)</p>
        <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
          {(link.admin_notes || []).map((n) => <li key={n.slice(0, 20)}>{n}</li>)}
        </ul>
        <p className="font-mono text-[10px] text-muted-foreground mt-4">API : {ADMIN_API}/nda</p>
      </div>
    </div>
  );
}
